import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  BuildFilterDto,
  createBuild,
  deleteBuild,
  findBuildsByQuery,
  getBuildById,
  updateBuild,
} from '@/services/CheckoutFlow/checkoutFlow.service';
import { TBuild } from '@/types/build.type';
import { PaginationResponse } from '@/hooks/usePaginationFilter';
import { apiFetch } from '@/utils/apiWrapper';

export const checkOutFlowApiSlice = createApi({
  reducerPath: 'checkoutFlowApi',
  tagTypes: ['Builds'],
  baseQuery: fakeBaseQuery(),
  endpoints: (builder) => ({
    getBuildsByQuery: builder.query<
      PaginationResponse<TBuild>,
      { companyId: string; organizationId: string; filters?: BuildFilterDto }
    >({
      async queryFn({ companyId, organizationId, filters }) {
        try {
          const data = await findBuildsByQuery(companyId, organizationId, filters);
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      providesTags: ['Builds'],
    }),
    getBuildById: builder.query<TBuild, { companyId: string; id: string }>({
      async queryFn({ companyId, id }) {
        try {
          const data = await getBuildById(companyId, id);
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      providesTags: ['Builds'],
    }),
    createBuild: builder.mutation<TBuild, { organizationId: string; buildData: Partial<TBuild> }>({
      async queryFn({ organizationId, buildData }) {
        try {
          const data = await createBuild(organizationId, buildData);
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      invalidatesTags: ['Builds'],
    }),
    updateBuild: builder.mutation<
      TBuild,
      { buildId: string; companyId: string; updates: Partial<TBuild> }
    >({
      async queryFn({ buildId, companyId, updates }) {
        try {
          const data = await updateBuild(buildId, companyId, updates);
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      invalidatesTags: ['Builds'],
    }),
    deleteBuild: builder.mutation<TBuild, { companyId: string; buildId: string }>({
      async queryFn({ companyId, buildId }) {
        const data = await deleteBuild(companyId, buildId);
        return { data };
      },
      invalidatesTags: ['Builds'],
    }),
    duplicateBuild: builder.mutation<TBuild, { buildId: string; companyId: string }>({
      async queryFn({ buildId, companyId }) {
        try {
          const response = await apiFetch(
            `${import.meta.env.VITE_URL_HUB_API}cdk/build/duplicate/${companyId}`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ buildId, companyId }),
            },
          );

          if (!response || !response.ok) {
            throw new Error(`Error duplicating build! ${response?.statusText} ${response?.status}`);
          }

          const data = await response.json();
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      invalidatesTags: ['Builds'],
    }),
    getPresets: builder.query<
      { data: TBuild[]; count: number },
      { companyId: string; filters?: any; page?: number; limit?: number }
    >({
      async queryFn({ companyId, filters, page, limit }) {
        try {
          let url = `${import.meta.env.VITE_URL_HUB_API}cdk/build/presets/company/${companyId}`;
          const params = new URLSearchParams();

          if (filters) {
            Object.entries(filters).forEach(([key, value]) => {
              if (value !== undefined && value !== null && value !== '') {
                params.append(key, String(value));
              }
            });
          }

          if (page !== undefined) params.append('page', String(page));
          if (limit !== undefined) params.append('limit', String(limit));

          if (params.toString()) url += `?${params.toString()}`;

          const response = await apiFetch(url);

          if (!response || !response.ok) {
            throw new Error(`Error fetching presets! ${response?.statusText} ${response?.status}`);
          }

          const responseData = await response.json();
          return {
            data: {
              data: responseData.data || responseData,
              count: responseData.count || responseData.length || 0,
            },
          };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      providesTags: ['Builds'],
    }),
    getPresetById: builder.query<TBuild, { companyId: string; id: string }>({
      async queryFn({ companyId, id }) {
        try {
          const response = await apiFetch(
            `${import.meta.env.VITE_URL_HUB_API}cdk/build/presets/${companyId}/${id}`,
          );

          if (!response || !response.ok) {
            throw new Error(
              `Error fetching preset by ID! ${response?.statusText} ${response?.status}`,
            );
          }

          const responseData = await response.json();
          return { data: responseData };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      providesTags: ['Builds'],
    }),
    getInstalledPresets: builder.query<
      { data: TBuild[]; count: number },
      {
        offset?: number;
        limit?: number;
        isPrintBlocks?: boolean;
        organizationId: string;
      }
    >({
      async queryFn({ offset, limit, isPrintBlocks, organizationId }) {
        try {
          let url = `${import.meta.env.VITE_URL_HUB_API}cdk/build/presets/installed/${organizationId}`;
          const params = new URLSearchParams();

          if (offset !== undefined) params.append('offset', String(offset));
          if (limit !== undefined) params.append('limit', String(limit));
          if (isPrintBlocks !== undefined) params.append('isPrintBlocks', String(isPrintBlocks));

          if (params.toString()) url += `?${params.toString()}`;
          console.log(url);
          const response = await apiFetch(url);

          if (!response || !response.ok) {
            throw new Error(
              `Error fetching installed presets! ${response?.statusText} ${response?.status}`,
            );
          }

          const responseData = await response.json();
          return {
            data: {
              data: responseData.data || [],
              count: responseData.count || 0,
            },
          };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      providesTags: ['Builds'],
    }),
    installPreset: builder.mutation<
      { status: string; message: string; presetId: string },
      { buildId: string; companyId: string }
    >({
      async queryFn({ buildId, companyId }) {
        try {
          const response = await apiFetch(
            `${import.meta.env.VITE_URL_HUB_API}cdk/build/presets/install/${companyId}`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ buildId, companyId }),
            },
          );

          if (!response || !response.ok) {
            throw new Error(`Error installing preset! ${response?.statusText} ${response?.status}`);
          }

          const responseData = await response.json();
          return { data: responseData };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      invalidatesTags: ['Builds'],
    }),
    uninstallPreset: builder.mutation<
      { status: string; message: string },
      { buildId: string; companyId: string }
    >({
      async queryFn({ buildId, companyId }) {
        try {
          const response = await apiFetch(
            `${import.meta.env.VITE_URL_HUB_API}cdk/build/presets/uninstall/${companyId}`,
            {
              method: 'DELETE',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ buildId, companyId }),
            },
          );

          if (!response || !response.ok) {
            throw new Error(
              `Error uninstalling preset! ${response?.statusText} ${response?.status}`,
            );
          }

          const responseData = await response.json();
          return { data: responseData };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      invalidatesTags: ['Builds'],
    }),
    clonePreset: builder.mutation<
      { status: string; message: string; buildId: string },
      { buildId: string; companyId: string }
    >({
      async queryFn({ buildId, companyId }) {
        try {
          const response = await apiFetch(
            `${import.meta.env.VITE_URL_HUB_API}cdk/build/presets/clone/${companyId}`,
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ buildId, companyId }),
            },
          );

          if (!response || !response.ok) {
            throw new Error(`Error cloning preset! ${response?.statusText} ${response?.status}`);
          }

          const responseData = await response.json();
          return { data: responseData };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      invalidatesTags: ['Builds'],
    }),
    getBuildsAndInstalledPresets: builder.query<
      { data: TBuild[]; installed: TBuild[]; count: number },
      {
        companyId: string;
        organizationId: string;
        filters?: BuildFilterDto;
        isPrintBlocks?: boolean;
      }
    >({
      async queryFn({ companyId, organizationId, filters = {}, isPrintBlocks }) {
        try {
          const totalOffset = filters.offset ?? 0;
          const totalLimit = filters.limit ?? 20;

          let presetsUrl = `${import.meta.env.VITE_URL_HUB_API}cdk/build/presets/installed/${organizationId}`;
          const presetsParams = new URLSearchParams();
          if (isPrintBlocks !== undefined)
            presetsParams.append('isPrintBlocks', String(isPrintBlocks));
          presetsParams.append('offset', String(totalOffset));
          presetsParams.append('limit', String(totalLimit));
          if (presetsParams.toString()) presetsUrl += `?${presetsParams.toString()}`;

          const presetsResp = await apiFetch(presetsUrl);
          if (!presetsResp || !presetsResp.ok) {
            throw new Error(
              `Error fetching installed presets! ${presetsResp?.statusText} ${presetsResp?.status}`,
            );
          }
          const presetsJson = await presetsResp.json();
          const presetsData: TBuild[] = presetsJson.data || [];
          const presetsCount = presetsJson.count || 0; // Total count of all presets

          const buildsCountRes = await findBuildsByQuery(companyId, organizationId, {
            ...filters,
            offset: 0,
            limit: 1, // Just to get count
          });
          const buildsCount = buildsCountRes.count || 0;
          const totalCount = presetsCount + buildsCount;

          const finalData: TBuild[] = [...presetsData];
          let allInstalledPresets: TBuild[] = [];

          const receivedFromPresets = presetsData.length;
          const stillNeed = totalLimit - receivedFromPresets;

          if (stillNeed > 0 && totalOffset + receivedFromPresets < totalCount) {
            // We need more data from builds
            const buildsOffset = Math.max(0, totalOffset - presetsCount);
            const buildsRes = await findBuildsByQuery(companyId, organizationId, {
              ...filters,
              offset: buildsOffset,
              limit: stillNeed,
            });
            const buildsData: TBuild[] = buildsRes.data || [];
            finalData.push(...buildsData);
          }

          if (totalOffset === 0) {
            allInstalledPresets = presetsData;
          } else {
            let allPresetsUrl = `${import.meta.env.VITE_URL_HUB_API}cdk/build/presets/installed/${companyId}`;
            const allPresetsParams = new URLSearchParams();
            if (isPrintBlocks !== undefined)
              allPresetsParams.append('isPrintBlocks', String(isPrintBlocks));
            if (allPresetsParams.toString()) allPresetsUrl += `?${allPresetsParams.toString()}`;

            const allPresetsResp = await apiFetch(allPresetsUrl);
            if (allPresetsResp && allPresetsResp.ok) {
              const allPresetsJson = await allPresetsResp.json();
              allInstalledPresets = allPresetsJson.data || [];
            }
          }

          return {
            data: {
              data: finalData,
              installed: allInstalledPresets,
              count: totalCount,
            },
          };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      providesTags: ['Builds'],
    }),
  }),
});

export const {
  useGetBuildsByQueryQuery,
  useLazyGetBuildsByQueryQuery,
  useCreateBuildMutation,
  useGetBuildByIdQuery,
  useUpdateBuildMutation,
  useDeleteBuildMutation,
  useDuplicateBuildMutation,
  useGetPresetsQuery,
  useGetPresetByIdQuery,
  useGetInstalledPresetsQuery,
  useInstallPresetMutation,
  useUninstallPresetMutation,
  useClonePresetMutation,
  useGetBuildsAndInstalledPresetsQuery,
} = checkOutFlowApiSlice;

export default checkOutFlowApiSlice;
